/**
 * Shop by Pro — kit multi-add (custom Padel USA).
 * <pro-kit-add> wraps the curated kit list on /pages/pros/{handle}.
 * Mirrors the kit-upsell.js pattern: batch POST to /cart/add.js, then navigate to /cart.
 * Docs: custom-code-padelusa/features/shop-by-pro.md
 */
(() => {
  if (customElements.get('pro-kit-add')) return;

  const CART_ADD_URL = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const CART_URL = '/cart';

  customElements.define(
    'pro-kit-add',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-pro-kit-submit]');
        this.totalEl = this.querySelector('[data-pro-kit-total]');
        this.boxes = [...this.querySelectorAll('input[data-pro-kit-item]')];
        if (!this.button) return;

        this.format = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });

        this.boxes.forEach((box) => box.addEventListener('change', () => this.#refresh()));
        this.querySelectorAll('select[data-pro-kit-size]').forEach((sel) =>
          sel.addEventListener('change', () => this.#pickSize(sel))
        );
        this.button.addEventListener('click', () => this.#addKit());
        this.#refresh();
      }

      /* Sized items (shoes): no size chosen = checkbox disabled = skipped from the batch. */
      #pickSize(sel) {
        const box = sel.closest('.pro-kit__row')?.querySelector('input[data-pro-kit-item]');
        if (!box) return;
        const opt = sel.selectedOptions[0];
        if (!opt || !opt.value) {
          box.checked = false;
          box.disabled = true;
        } else {
          box.dataset.variantId = opt.value;
          if (opt.dataset.price) box.dataset.priceCents = opt.dataset.price;
          box.disabled = false;
          box.checked = true;
        }
        this.#refresh();
      }

      #selected() {
        return this.boxes.filter((box) => box.checked && !box.disabled);
      }

      #refresh() {
        const items = this.#selected();
        const cents = items.reduce((sum, box) => sum + (Number(box.dataset.priceCents) || 0), 0);
        if (this.totalEl) this.totalEl.textContent = this.format.format(cents / 100);
        this.button.disabled = items.length === 0;
        const count = this.button.querySelector('[data-pro-kit-count]');
        if (count) count.textContent = items.length;
      }

      async #addKit() {
        const items = this.#selected().map((box) => ({
          id: Number(box.dataset.variantId),
          quantity: 1,
        }));
        if (items.length === 0) return;

        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        this.button.classList.add('pro-kit__submit--loading');
        try {
          await fetch(CART_ADD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          /* Success micro-animation, then redirect to the cart (contract unchanged). */
          this.#showAdded(items.length);
          await new Promise((resolve) => setTimeout(resolve, 700));
        } catch (error) {
          /* navigate regardless — cart page shows the truth */
        }
        window.location.href = CART_URL;
      }

      /* Swap the button into a green "Added ✓" confirmation state (checkmark lives
         in the markup; revealed by CSS via the --added class — no innerHTML). */
      #showAdded(count) {
        this.button.removeAttribute('aria-busy');
        this.button.classList.remove('pro-kit__submit--loading');
        this.button.classList.add('pro-kit__submit--added');
        const meta = this.button.querySelector('[data-pro-kit-meta]');
        if (meta) meta.hidden = true;
        const text = this.button.querySelector('[data-pro-kit-text]');
        if (text) text.textContent = count > 1 ? `Added ${count} items` : 'Added to cart';
      }
    }
  );
})();
